import { Component, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GraphComponent } from '../visualizations/graph/graph.component';
import { GraphRelation } from '../../interfaces/visualization.interface';
import { mapRelationsToCytoscapeElements } from '../visualizations/graph.mapper';
import { ElementDefinition } from 'cytoscape';
import { Inference, InferencesTab } from './inferences.types';
import { VisualizationService } from '../../services/visualization.service';

@Component({
  selector: 'app-inferences',
  standalone: true,
  imports: [CommonModule, FormsModule, GraphComponent],
  templateUrl: './inferences.component.html',
  styleUrl: './inferences.component.css'
})
export class InferencesComponent implements OnInit {
  @Output() backClick = new EventEmitter<void>();

  inferences: Inference[] = [];
  filteredInferences: Inference[] = [];
  isLoading = true;
  error: string | null = null;
  isSyncing = false;
  private _graphElements: ElementDefinition[] = [];

  activeTab: InferencesTab = 'recent';

  // Detail panel state
  selectedInference: Inference | null = null;
  detailGraphElements: ElementDefinition[] = [];
  isGraphFullScreen = false;

  // Filter state
  selectedSeverity: string = 'All';
  selectedType: string = 'All';
  selectedConfidence: string = 'All';
  selectedCluster: string = 'All';
  searchQuery: string = '';

  // Dynamic filter options
  severityOptions: string[] = [];
  typeOptions: string[] = [];
  clusterOptions: { value: string; label: string }[] = [{ value: 'All', label: 'All' }];
  confidenceOptions: string[] = ['All', 'High (80-100)', 'Medium (50-79)', 'Low (0-49)'];

  // Status section
  selectedStatus: string = 'New';
  statusOptions: string[] = ['New', 'Under Review', 'Approved', 'Rejected'];

  // Severity sort order: critical first, then high, medium, low
  private severityPriority: Record<string, number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3
  };

  constructor(private visualizationService: VisualizationService) {}

  ngOnInit(): void {
    this.loadInferences();
  }

  get graphElements(): ElementDefinition[] {
    return this._graphElements;
  }

  get tabInferences(): Inference[] {
    return this.inferences.filter(i => this.activeTab === 'recent' ? !!i.is_recent : !i.is_recent);
  }

  get recentCount(): number {
    return this.inferences.filter(i => i.is_recent).length;
  }

  get historyCount(): number {
    return this.inferences.filter(i => !i.is_recent).length;
  }

  get tabEmptyMessage(): string {
    if (this.activeTab === 'recent') {
      return 'No recent inferences. Generate from a cluster to see them here. Previous results are in History.';
    }
    return 'No earlier inferences yet.';
  }

  loadInferences(): void {
    this.isLoading = true;
    this.error = null;

    this.visualizationService.getInferences().subscribe({
      next: (response: any) => {
        const inferences = this.extractInferences(response);
        this.setInferences(inferences);
        if (inferences.length === 0 && response?.message && !this.hasSuccessPayload(response)) {
          this.error = response.message;
        }
      },
      error: (err) => {
        console.error('Failed to load inferences:', err);
        this.error = 'Failed to load inferences. Please try again.';
        this.isLoading = false;
      }
    });
  }

  syncInferences(): void {
    this.isSyncing = true;
    this.error = null;

    this.visualizationService.syncInferences().subscribe({
      next: (response: any) => {
        const inferences = this.extractInferences(response);
        this.setInferences(inferences);
        if (inferences.length === 0) {
          this.error = response?.message || 'No inferences found after sync';
        }
        this.isSyncing = false;
      },
      error: (err) => {
        console.error('Failed to sync inferences:', err);
        this.error = 'Failed to sync inferences. Please try again.';
        this.isSyncing = false;
      }
    });
  }

  private hasSuccessPayload(response: any): boolean {
    return response?.isSuccess === true || response?.hasResult === true || response?.data?.hasResult === true;
  }

  private extractInferences(response: any): Inference[] {
    let inferences: Inference[] = [];

    if (response?.result?.result && Array.isArray(response.result.result)) {
      inferences = response.result.result;
    } else if (response?.data?.result?.result && Array.isArray(response.data.result.result)) {
      inferences = response.data.result.result;
    } else if (response?.data?.result && Array.isArray(response.data.result)) {
      inferences = response.data.result;
    } else if (response?.data && Array.isArray(response.data)) {
      inferences = response.data;
    } else if (Array.isArray(response)) {
      inferences = response;
    }

    return inferences.map(item => this.normalizeInference(item));
  }

  private normalizeInference(item: any): Inference {
    return {
      ...item,
      id: item.id ?? item.Id,
      supporting_facts: Array.isArray(item.supporting_facts) ? item.supporting_facts : (item.supportingFacts ?? []),
      created_at: item.created_at ?? item.createdAt,
      cluster_id: item.cluster_id ?? item.clusterId ?? null,
      cluster_run_id: item.cluster_run_id ?? item.clusterRunId ?? null,
      generation_id: item.generation_id ?? item.generationId ?? null,
      is_recent: item.is_recent === true || item.isRecent === true
    };
  }

  private setInferences(inferences: Inference[]): void {
    this.inferences = inferences;
    this.sortBySeverity(this.inferences);
    this.severityOptions = ['All', ...new Set(this.inferences.map(i => this.capitalize(i.severity)))];
    this.typeOptions = ['All', ...new Set(this.inferences.map(i => i.inference_type))];
    this.rebuildClusterOptions();
    this.applyFilters();
    this.processGraphData();
  }

  private rebuildClusterOptions(): void {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [{ value: 'All', label: 'All' }];
    let hasUnknown = false;

    for (const inference of this.inferences) {
      if (inference.cluster_id == null && !inference.cluster_run_id) {
        hasUnknown = true;
        continue;
      }
      const value = this.clusterFilterValue(inference);
      if (seen.has(value)) {
        continue;
      }
      seen.add(value);
      options.push({ value, label: this.clusterLabel(inference) });
    }

    options.sort((a, b) => {
      if (a.value === 'All') return -1;
      if (b.value === 'All') return 1;
      return a.label.localeCompare(b.label, undefined, { numeric: true });
    });

    if (hasUnknown) {
      options.push({ value: 'unknown', label: 'Unknown cluster' });
    }

    this.clusterOptions = options;
  }

  selectTab(tab: InferencesTab): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.closeDetailPanel();
    this.applyFilters();
    this.processGraphData();
  }

  private processGraphData(): void {
    const source = this.tabInferences;
    if (source && source.length > 0) {
      const relations: GraphRelation[] = source.flatMap(inference =>
        (inference.supporting_facts || []).map(fact => ({
          source: fact.source,
          relationship: fact.relationship,
          target: fact.target
        }))
      );
      this._graphElements = mapRelationsToCytoscapeElements(relations);
    } else {
      this._graphElements = [];
    }
    this.isLoading = false;
  }

  private sortBySeverity(inferences: Inference[]): void {
    inferences.sort((a, b) => {
      const priorityA = this.severityPriority[a.severity.toLowerCase()] ?? 99;
      const priorityB = this.severityPriority[b.severity.toLowerCase()] ?? 99;
      return priorityA - priorityB;
    });
  }

  applyFilters(): void {
    const query = this.searchQuery.trim().toLowerCase();

    this.filteredInferences = this.tabInferences.filter(inference => {
      if (this.selectedCluster !== 'All') {
        if (this.selectedCluster === 'unknown') {
          if (inference.cluster_id != null || inference.cluster_run_id) return false;
        } else if (this.clusterFilterValue(inference) !== this.selectedCluster) {
          return false;
        }
      }
      if (this.selectedSeverity !== 'All' && this.capitalize(inference.severity) !== this.selectedSeverity) {
        return false;
      }
      if (this.selectedType !== 'All' && inference.inference_type !== this.selectedType) {
        return false;
      }
      if (this.selectedConfidence !== 'All') {
        const confPct = this.getConfidencePercentage(inference.confidence);
        if (this.selectedConfidence === 'High (80-100)' && confPct < 80) return false;
        if (this.selectedConfidence === 'Medium (50-79)' && (confPct < 50 || confPct > 79)) return false;
        if (this.selectedConfidence === 'Low (0-49)' && confPct > 49) return false;
      }
      if (query) {
        const topic = (inference.topic || '').toLowerCase();
        const description = (inference.inference || '').toLowerCase();
        if (!topic.includes(query) && !description.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  highlightSearchTerm(text: string | null | undefined): string {
    const safeText = this.escapeHtml(text ?? '');
    const query = this.searchQuery.trim();

    if (!query) {
      return safeText;
    }

    const safeQuery = this.escapeHtml(query);
    const pattern = new RegExp(`(${this.escapeRegExp(safeQuery)})`, 'gi');
    return safeText.replace(pattern, '<mark class="search-highlight">$1</mark>');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  getSeverityClass(severity: string): string {
    const s = severity.toLowerCase();
    switch (s) {
      case 'critical': return 'severity-critical';
      case 'high': return 'severity-high';
      case 'medium': return 'severity-medium';
      case 'low': return 'severity-low';
      default: return '';
    }
  }

  getConfidencePercentage(confidence: number): number {
    return Math.round(confidence * 100);
  }

  getCountBySeverity(severity: string): number {
    return this.tabInferences.filter(i => i.severity.toLowerCase() === severity).length;
  }

  getSeverityIcon(severity: string): string {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'assets/icons/inferences/critical-v2.svg';
      case 'high':
        return 'assets/icons/inferences/high-v2.svg';
      case 'medium':
        return 'assets/icons/inferences/medium-v2.svg';
      case 'low':
        return 'assets/icons/inferences/low-v2.svg';
      default:
        return 'assets/icons/inferences/medium-v2.svg';
    }
  }

  clusterLabel(inference: Inference | null | undefined): string {
    if (inference?.cluster_id == null && !inference?.cluster_run_id) {
      return 'Unknown cluster';
    }
    const name = inference?.cluster_id == null ? 'Cluster' : `Cluster ${inference.cluster_id}`;
    const shortId = this.shortClusterRunId(inference?.cluster_run_id);
    return shortId ? `${name} · ${shortId}` : name;
  }

  private clusterFilterValue(inference: Inference): string {
    if (inference.cluster_id == null && !inference.cluster_run_id) {
      return 'unknown';
    }
    return `${inference.cluster_id ?? ''}|${inference.cluster_run_id ?? ''}`;
  }

  private shortClusterRunId(runId?: string | null): string {
    const id = (runId || '').trim();
    return id ? id.slice(0, 8) : '';
  }

  formatRelativeTime(iso?: string | null): string {
    const date = this.parseUtcTimestamp(iso);
    if (!date) {
      return '';
    }

    const diffMs = Date.now() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    if (diffSec < 45) {
      return 'Just now';
    }
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) {
      return `${diffMin} min ago`;
    }
    const diffHour = Math.round(diffMin / 60);
    if (diffHour < 24) {
      return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
    }
    const diffDay = Math.round(diffHour / 24);
    if (diffDay < 7) {
      return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
    }
    return date.toLocaleDateString();
  }

  formatAbsoluteTime(iso?: string | null): string {
    const date = this.parseUtcTimestamp(iso);
    if (!date) {
      return '';
    }
    return date.toLocaleString();
  }

  // CreatedAt is stored as UTC. SQL/JSON often omit the Z, and the browser would
  // then treat it as local (UTC+5 here → "5 hours ago").
  private parseUtcTimestamp(iso?: string | null): Date | null {
    if (!iso) {
      return null;
    }
    const trimmed = iso.trim();
    if (!trimmed) {
      return null;
    }
    const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
    const date = new Date(hasZone ? trimmed : `${trimmed}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  isSelected(inference: Inference): boolean {
    if (!this.selectedInference) {
      return false;
    }
    if (inference.id != null && this.selectedInference.id != null) {
      return inference.id === this.selectedInference.id;
    }
    return this.selectedInference === inference;
  }

  onBackClick(): void {
    this.backClick.emit();
  }

  selectInference(inference: Inference): void {
    this.selectedInference = inference;
    const relations: GraphRelation[] = (inference.supporting_facts || []).map(fact => ({
      source: fact.source,
      relationship: fact.relationship,
      target: fact.target
    }));
    this.detailGraphElements = mapRelationsToCytoscapeElements(relations);
  }

  closeDetailPanel(): void {
    this.selectedInference = null;
    this.detailGraphElements = [];
    this.isGraphFullScreen = false;
  }

  toggleGraphFullScreen(): void {
    this.isGraphFullScreen = !this.isGraphFullScreen;
  }

  getDocumentIcon(index: number): string {
    const colors = ['#4A90E2', '#D92D2D', '#6B7280'];
    return colors[index % colors.length];
  }

  formatTypeName(type: string): string {
    return type.replace(/_/g, ' ');
  }

  hasDocumentSources(): boolean {
    return this.selectedInference?.sources !== undefined &&
           this.selectedInference.sources !== null &&
           this.selectedInference.sources.length > 0 || false;
  }

  onStatusChange(status: string): void {
    this.selectedStatus = status;
    console.log('Status changed to:', status);
  }

  acceptInference(): void {
    console.log('Accept action triggered');
  }

  rejectInference(): void {
    console.log('Reject action triggered');
  }

  assignInference(): void {
    console.log('Assign action triggered');
  }
}
